import { useState, useEffect, useRef } from 'react';
import { Search, Loader2, Check, X, Sparkles, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function ICDCodeSuggester({ 
  value = '', 
  onChange, 
  onSelect,
  placeholder = "Enter diagnosis...",
  className = ""
}) {
  const [diagnosis, setDiagnosis] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (diagnosis.length >= 3) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(diagnosis);
      }, 500);
    } else {
      setSuggestions([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [diagnosis]);

  const fetchSuggestions = async (text) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/ai/icd-suggest', { diagnosis: text });
      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
        setShowDropdown(true);
      } else {
        setError(response.data.error || 'Failed to get suggestions');
        setSuggestions([]);
      }
    } catch (err) {
      console.error('ICD suggestion error:', err);
      setError('Failed to fetch ICD suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCode = (code) => {
    const newSelected = [...selectedCodes, code];
    setSelectedCodes(newSelected);
    if (onSelect) {
      onSelect(code, newSelected);
    }
    setShowDropdown(false);
  };

  const handleRemoveCode = (codeToRemove) => {
    const newSelected = selectedCodes.filter(c => c.code !== codeToRemove.code);
    setSelectedCodes(newSelected);
    if (onSelect) {
      onSelect(null, newSelected);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600 bg-green-50';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected Codes */}
      {selectedCodes.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedCodes.map((code) => (
            <div 
              key={code.code}
              className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-lg text-sm"
            >
              <span className="font-mono font-semibold">{code.code}</span>
              <span className="text-purple-600">-</span>
              <span className="truncate max-w-[200px]">{code.description}</span>
              <button
                onClick={() => handleRemoveCode(code)}
                className="ml-1 hover:bg-purple-200 rounded p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-purple-500" />
        <Input
          ref={inputRef}
          value={diagnosis}
          onChange={(e) => {
            setDiagnosis(e.target.value);
            if (onChange) onChange(e.target.value);
          }}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
          data-testid="icd-diagnosis-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && (suggestions.length > 0 || error) && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto"
          data-testid="icd-suggestions-dropdown"
        >
          {error ? (
            <div className="p-3 text-center text-red-600 flex items-center justify-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          ) : (
            <>
              <div className="p-2 bg-slate-50 border-b text-xs text-slate-500 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                AI-suggested ICD-10 codes for "{diagnosis}"
              </div>
              {suggestions.map((suggestion, index) => {
                const isSelected = selectedCodes.some(c => c.code === suggestion.code);
                return (
                  <button
                    key={suggestion.code}
                    onClick={() => !isSelected && handleSelectCode(suggestion)}
                    disabled={isSelected}
                    className={`w-full text-left p-3 hover:bg-slate-50 border-b last:border-b-0 transition-colors ${
                      isSelected ? 'bg-green-50 cursor-default' : 'cursor-pointer'
                    }`}
                    data-testid={`icd-suggestion-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-purple-700">
                          {suggestion.code}
                        </span>
                        {isSelected && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getConfidenceColor(suggestion.confidence)}`}>
                        {suggestion.confidence}% match
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {suggestion.description}
                    </p>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Helper Text */}
      {!showDropdown && diagnosis.length > 0 && diagnosis.length < 3 && (
        <p className="text-xs text-slate-400 mt-1">
          Type at least 3 characters to get ICD code suggestions
        </p>
      )}
    </div>
  );
}
